// The tracking screens' own reads (UI-7): Kézbesítés, Őrszem, Riportok.
//
// A file of its own rather than more of `lib/api.ts`, so the slices building
// other screens beside this one do not edit the same lines. The older reads
// these screens also use (`fetchAlerts`, `fetchReputation`, …) stay where
// they were.

import { request } from "@/lib/api";
import type { AnomalyCell, CampaignTags, PoolDay, Sources } from "@/lib/api";

// --- Kézbesítés ------------------------------------------------------------------

export interface LivePoolDay extends PoolDay {
  /** Counted from the source tables at the moment of the request, because the
   * morning aggregate could not hold the whole day yet. */
  live: boolean;
  /** When these figures were made: the request's moment for a live day, the
   * aggregator's last run otherwise. */
  recomputed_at: string | null;
}

/** Slice 5.4: one sending IP at one provider over the asked range. */
export interface EvidenceIpIsp {
  /** The IP; the SES host while its letter is not mapped; "" when unknown. */
  sending_ip: string;
  isp: string;
  /** Each message once, on the address that sent it: by SES's
   * `ses:outgoing-ip` tag where there is one, else delivered + first bounce. */
  sent: number;
  /** Of `sent`, how many stood on SES's own tag rather than the fallback. */
  sent_from_tag: number;
  delivered: number;
  deferred: number;
  bounced: number;
  complained: number;
  rejected: number;
  bounced_by_class: Record<string, number>;
  deferred_by_class: Record<string, number>;
}

export interface EvidenceClass {
  diagnostic_class: string;
  bounced: number;
  deferred: number;
}

export interface DeliveryEvidence {
  first_day: string;
  last_day: string;
  by_ip_isp: EvidenceIpIsp[];
  by_class: EvidenceClass[];
  live_days: string[];
  recomputed_at: string | null;
}

export function fetchEvidence(params: { from: string; to: string }): Promise<DeliveryEvidence> {
  const query = new URLSearchParams({ from: params.from, to: params.to });
  return request<DeliveryEvidence>(`/deliverability/evidence?${query.toString()}`);
}

export function fetchDailyLive(params: {
  from: string;
  to: string;
  pool?: string;
}): Promise<LivePoolDay[]> {
  const query = new URLSearchParams({ from: params.from, to: params.to, live: "true" });
  if (params.pool) query.set("pool", params.pool);
  return request<LivePoolDay[]>(`/deliverability/daily?${query.toString()}`);
}

export interface PoolCountry {
  country: string;
  timezone: string;
}

export interface PoolMeta {
  pool: string;
  name: string;
  ip_count: number;
  countries: PoolCountry[];
  warmup_state: string;
  watch_state: string;
  is_paused: boolean;
  paused_reason: string | null;
  throttle_factor: number;
  daily_limit_per_ip: number;
}

export function fetchPoolMeta(): Promise<PoolMeta[]> {
  return request<PoolMeta[]>("/deliverability/pools");
}

export interface DnsblIp {
  ip: string;
  level: string;
  value: string;
  observed_at: string;
  listed_on: string[];
  unverifiable: string[];
  zones: string[];
  checks: number;
  listed_checks: number;
}

export function fetchDnsbl(days = 2): Promise<DnsblIp[]> {
  return request<DnsblIp[]>(`/deliverability/dnsbl?days=${days}`);
}

/** The typo-domain rule (owner, 2026-10-30): what it did, what it waits for. */
export interface TypoDomainRow {
  domain: string;
  state: string;
  target: string | null;
  contacts: number;
  /** The DNS answer in a word: nxdomain, null_mx, no_mx_no_address, … */
  why: string | null;
  candidates: string[];
  twin: number;
  corrected: number;
  dead: number;
}

export interface TypoDomains {
  outcomes: { twin: number; corrected: number; dead: number };
  superseded: number;
  /** Per domain state: the domains the contact base uses, and their rows. */
  states: Record<string, { domains: number; contacts: number }>;
  last_checked_at: string | null;
  last_decided_at: string | null;
  domains: TypoDomainRow[];
}

export function fetchTypoDomains(): Promise<TypoDomains> {
  return request<TypoDomains>("/deliverability/typo-domains");
}

export interface DmarcDomain {
  domain: string;
  /** Reports read; the figures are theirs. */
  reports: number;
  /** Reports the window holds: more than `reports` when the read was capped. */
  reports_in_window: number;
  reporters: string[];
  messages: number;
  aligned: number;
  unaligned: number;
  unaligned_sources: { source_ip: string; messages: number }[];
  first_begin_at: string;
  last_end_at: string;
}

export function fetchDmarc(days = 14): Promise<DmarcDomain[]> {
  return request<DmarcDomain[]>(`/deliverability/dmarc?days=${days}`);
}

// --- Őrszem -------------------------------------------------------------------------

export interface WatchSource {
  source: string;
  last_run_at: string;
  last_status: string;
  signals: number | null;
}

export function fetchWatchSources(): Promise<WatchSource[]> {
  return request<WatchSource[]>("/watchdog/sources");
}

export interface TimelineSignal {
  id: number;
  source: string;
  subject_kind: string;
  subject: string;
  metric: string;
  value: string;
  level: string;
  observed_at: string;
  injected: boolean;
}

export interface TimelineRule {
  source: string;
  metric: string;
  level: string;
  action: "throttle" | "pause" | string;
  release: "auto" | "manual" | string;
}

export interface TimelineEntry {
  kind: "decision" | "signal";
  occurred_at: string;
  pool: string | null;
  action: string | null;
  from_state: string | null;
  to_state: string | null;
  actor: string | null;
  reason: string | null;
  injected: boolean;
  signal: TimelineSignal | null;
  rule: TimelineRule | null;
}

/** One 2.6 rule's finding on one cell of the deviation grid. */
export interface AnomalyRule {
  rule: string;
  level: string;
  threshold: number | null;
  /** Why the rule said nothing: "night", "small sample", "no profile", … */
  skipped: string;
}

export interface JudgedAnomalyCell extends AnomalyCell {
  /** null: the loop has not judged this pool's hour yet — not "all fine". */
  rules: AnomalyRule[] | null;
}

export function fetchAnomalyCells(pool?: string): Promise<JudgedAnomalyCell[]> {
  const query = pool ? `?pool=${encodeURIComponent(pool)}` : "";
  return request<JudgedAnomalyCell[]>(`/watchdog/anomaly${query}`);
}

export function fetchTimeline(params: {
  pool?: string;
  source?: string;
  days?: number;
  limit?: number;
}): Promise<TimelineEntry[]> {
  const query = new URLSearchParams();
  if (params.pool) query.set("pool", params.pool);
  if (params.source) query.set("source", params.source);
  if (params.days) query.set("days", String(params.days));
  if (params.limit) query.set("limit", String(params.limit));
  const suffix = query.toString();
  return request<TimelineEntry[]>(`/watchdog/timeline${suffix ? `?${suffix}` : ""}`);
}

// --- Riportok -------------------------------------------------------------------

/** The Postgres half of a campaign's figures: always there. */
export interface PostgresFigures {
  pending: number;
  sent: number;
  unknown: number;
  failed: number;
  skipped: number;
  skipped_suppressed: number;
  skipped_hygiene: number;
  /** Left to Klaviyo between assembly and send (0028). */
  skipped_handover?: number;
  cancelled: number;
  delivered: number;
  bounced: number;
  complained: number;
  unsubscribed: number;
  orders: number;
  revenue_eur_cents: number;
  /** The ECB days the euro revenue was converted at (each order at its own
   * day's rate); null when nothing was converted. */
  fx_first_day?: string | null;
  fx_last_day?: string | null;
  by_window: Record<
    string,
    { orders: number; revenue_eur_cents: number; fx_first_day?: string | null; fx_last_day?: string | null }
  >;
  // Per thousand of `sent`; null when nothing was sent.
  delivered_permille: number | null;
  bounce_permille: number | null;
  complaint_permille: number | null;
  unsubscribe_permille: number | null;
}

/** The warehouse half: null as a whole when ClickHouse did not answer. */
export interface WarehouseFigures {
  unique_opens: number;
  unique_human_opens: number;
  total_opens: number;
  /** Every contact who opened, Apple's automatic opens included — the
   * headline — and of those, the ones only a machine opened for. */
  opens_total: number;
  opens_machine: number;
  unique_clicks: number;
  unique_human_clicks: number;
  // CTR over the Postgres sends, CTOR over the warehouse's own opens, the
  // open rate (opens_total) over the Postgres delivered.
  ctr_permille: number | null;
  ctor_permille: number | null;
  open_rate_permille: number | null;
}

export interface GroupCountryReport {
  country: string;
  campaign_id: number;
  name: string;
  state: string;
  started_at: string | null;
  finished_at: string | null;
  recipient_total: number;
  postgres: PostgresFigures;
  clickhouse: WarehouseFigures | null;
}

export interface VariantReport {
  key: string;
  subject: string;
  share_permille: number;
  /** Won in every country that ran the test. */
  is_winner: boolean;
  /** The countries whose verdict chose this arm: each country is decided on
   * its own figures, so two arms can each have won somewhere. */
  won_in: string[];
  /** Postgres: `campaign_recipient.variant_key`, the test group only. */
  sent: number;
  delivered: number;
  bounced: number;
  complained: number;
  unsubscribed: number;
  /** Postgres: `engagement_event`, one person once, machine opens left out —
   * the numbers the A/B verdict was reached on. */
  unique_human_opens: number;
  unique_human_clicks: number;
  orders: number;
  revenue_eur_cents: number;
}

export interface GroupTimelinePoint {
  hour: string;
  opens: number;
  clicks: number;
}

export interface GroupReport {
  group_id: number | null;
  campaign_id: number | null;
  name: string;
  state: string;
  mode: string | null;
  started_at: string | null;
  finished_at: string | null;
  tags: CampaignTags;
  countries: GroupCountryReport[];
  totals: { postgres: PostgresFigures; clickhouse: WarehouseFigures | null };
  ab: {
    metric: string | null;
    decided_at: string | null;
    /** The arm that won in every country, or null when they chose differently. */
    winner: string | null;
    /** country -> the arm its verdict chose. */
    winners: Record<string, string>;
    variants: VariantReport[];
  } | null;
  timeline: GroupTimelinePoint[] | null;
  primary_window: string;
  sources: Sources;
  source_unavailable: string[];
  clickhouse_error: string | null;
}

export interface GroupCard {
  group_id: number | null;
  campaign_id: number | null;
  name: string;
  state: string;
  countries: string[];
  started_at: string | null;
  totals: { postgres: PostgresFigures; clickhouse: WarehouseFigures | null };
}

export interface GroupCards {
  cards: GroupCard[];
  primary_window: string;
  sources: Sources;
  source_unavailable: string[];
  clickhouse_error: string | null;
}

export interface GroupCardFilters {
  country?: string;
  tag?: string;
  since?: string;
  until?: string;
}

export function fetchGroupCards(filters: GroupCardFilters): Promise<GroupCards> {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== "") query.set(key, value);
  }
  const suffix = query.toString();
  return request<GroupCards>(`/reports/groups${suffix ? `?${suffix}` : ""}`);
}

export function fetchGroupReport(groupId: number): Promise<GroupReport> {
  return request<GroupReport>(`/reports/groups/${groupId}`);
}

/** The report a country campaign belongs in: its group's, or its own. */
export function fetchCampaignGroupReport(campaignId: number): Promise<GroupReport> {
  return request<GroupReport>(`/reports/groups/campaign/${campaignId}`);
}

